import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Linkedin, Target, Globe, BarChart3, CheckCircle, Monitor } from "lucide-react";
import { Link } from "react-router-dom";

const B2BSolutions = () => {
  const solutions = [
    {
      icon: Linkedin,
      title: "LinkedIn SDR Campaigns",
      description: "Automated outreach that books qualified calls with decision-makers at your target accounts.",
      features: [
        "ICP-targeted prospecting",
        "Multi-touch sequences",
        "Response handling",
        "CRM integration"
      ],
      link: "/linkedin-sdr",
      badge: "High ROI",
      highlighted: true
    },
    {
      icon: Target,
      title: "Account-Based Marketing",
      description: "Coordinated campaigns targeting your highest-value accounts across multiple channels.",
      features: [
        "Account identification",
        "Personalized content",
        "Multi-channel orchestration",
        "Account scoring"
      ],
      link: "/enterprise",
      badge: "Enterprise"
    },
    {
      icon: Monitor,
      title: "Website Design",
      description: "Stunning, high-performance websites designed to convert visitors into qualified leads.",
      features: [
        "Custom web design",
        "Landing page optimization",
        "Mobile-first development",
        "Conversion rate optimization"
      ],
      link: "/website-design"
    },
    {
      icon: Globe,
      title: "B2B SEO & Content",
      description: "Search visibility and content strategy that captures enterprise buyers.",
      features: [
        "Technical SEO audits",
        "Content strategy",
        "Keyword optimization",
        "Lead capture systems"
      ],
      link: "/seo-local-search"
    },
    {
      icon: BarChart3,
      title: "Paid Media Campaigns",
      description: "Targeted advertising on LinkedIn, Google, and Meta that reaches B2B decision-makers.",
      features: [
        "LinkedIn advertising",
        "Google Ads management",
        "Retargeting campaigns",
        "Attribution tracking"
      ],
      link: "/paid-ads"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-primary text-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge variant="outline" className="bg-white/10 border-white/20 text-white mb-4">
              B2B Growth Solutions
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Full-Stack Demand Generation
            </h2>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Integrated solutions that work together to fill your pipeline with qualified enterprise opportunities.
            </p>
          </div>

          {/* Solutions Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <Card 
                  key={index} 
                  className={`bg-white text-foreground hover:shadow-xl transition-all duration-300 ${
                    solution.highlighted ? 'ring-2 ring-accent/50' : ''
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      {solution.badge && (
                        <Badge className={solution.highlighted ? 'bg-accent text-primary' : 'bg-secondary/20 text-secondary'}>
                          {solution.badge}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl">{solution.title}</CardTitle>
                    <CardDescription className="text-base">{solution.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {solution.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild variant="outline" className="w-full group">
                      <Link to={solution.link}>
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-white/70 mb-6">
              Not sure which solution is right for you? Let us analyze your current position.
            </p>
            <Button asChild size="lg" variant="secondary" className="group">
              <Link to="/do-you-qualify">
                Get a Custom Growth Plan
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BSolutions;
